<template>
  <div class="file-upload-session" v-if="visible">
    <div class="upload-modal-overlay" @click="handleOverlayClick">
      <div class="upload-modal" @click.stop>
        <div class="modal-header">
          <h3>Uploading Files</h3>
          <button class="close-btn" @click="handleClose" :disabled="isActivelyUploading">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Overall Progress -->
          <div class="overall-progress">
            <div class="progress-info">
              <span class="status-text">{{ overallStatusText }}</span>
              <i
                v-if="isInitializingUpload"
                class="bi bi-arrow-repeat init-upload-spinner"
                aria-label="Initializing upload"
              ></i>
              <span class="percent-text">{{ overallPercent }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: overallPercent + '%' }"></div>
            </div>
          </div>

          <!-- File List -->
          <div class="file-list">
            <div
              v-for="file in orderedFileStates"
              :key="file.blob_path"
              class="file-item"
              :class="'status-' + file.status"
            >
              <div class="file-icon">
                <i
                  class="bi"
                  :class="{
                    'bi-file-earmark': file.status === 'pending',
                    'bi-arrow-repeat spinner': file.status === 'uploading' || file.status === 'uploaded',
                    'bi-check-circle-fill': file.status === 'committed',
                    'bi-exclamation-circle-fill': file.status === 'failed'
                  }"
                ></i>
              </div>

              <div class="file-info">
                <div class="file-name">{{ file.filename }}</div>
                <div class="file-status-text">{{ getStatusText(file) }}</div>
                <div v-if="file.status === 'uploading'" class="file-progress-bar">
                  <div class="file-progress-fill" :style="{ width: file.progress + '%' }"></div>
                </div>
                <div v-if="file.error" class="file-error">{{ file.error }}</div>
              </div>

              <div class="file-actions">
                <button
                  v-if="file.status === 'failed'"
                  class="retry-btn"
                  @click="retryFile(file)"
                  :disabled="file.retrying"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-secondary"
            @click="handleFinalize"
            :disabled="isActivelyUploading"
          >
            {{ finalizeButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from "@/services/apiService";
import { useToast } from "vue-toastification";

export default {
  name: "FileUploadSession",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    fileType: {
      type: String,
      required: true,
    },
    entityId: {
      type: [Number, String],
      required: true,
    },
    files: {
      type: Array, // File objects
      required: true,
    },
    entraId: {
      type: String,
      required: true,
    },
    folderId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["complete", "close", "error"],
  data() {
    return {
      sessionId: null,
      fileStates: [], // { blob_path, filename, status, progress, error, uploadInfo, fileObject, retrying }
      overallPercent: 0,
      uploadedBytes: 0,
      totalBytes: 0,
      isInitializingUpload: false,
      toast: useToast(),
    };
  },
  computed: {
    orderedFileStates() {
      const failed = this.fileStates.filter((f) => f.status === "failed");
      const nonFailed = this.fileStates.filter((f) => f.status !== "failed");
      return [...failed, ...nonFailed];
    },
    isActivelyUploading() {
      return this.fileStates.some(
        (f) => f.status === "uploading" || f.status === "uploaded"
      );
    },
    overallStatusText() {
      const committed = this.fileStates.filter((f) => f.status === "committed").length;
      const failed = this.fileStates.filter((f) => f.status === "failed").length;
      const total = this.fileStates.length;

      if (total === 0) {
        return "Starting Upload";
      }

      if (committed === total) {
        return "All files uploaded successfully";
      } else if (failed > 0) {
        return `${committed} of ${total} files uploaded (${failed} failed)`;
      } else {
        return `Uploading ${committed} of ${total} files...`;
      }
    },
    finalizeButtonText() {
      const failed = this.fileStates.filter((f) => f.status === "failed").length;
      if (this.isActivelyUploading) {
        return "Uploading...";
      } else if (failed > 0) {
        return "Finalize (Skip Failed Files)";
      } else {
        return "Done";
      }
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && Array.isArray(this.files) && this.files.length > 0) {
        this.startUpload();
      }
    },
  },
  methods: {
    async startUpload() {
      this.isInitializingUpload = true;

      try {
        // Initialize session
        const initResponse = await apiService.initUploadSession(
          this.fileType,
          this.entityId,
          this.files,
          this.entraId,
          this.folderId
        );

        const sessionId = initResponse?.session_id;
        const uploadInfo = Array.isArray(initResponse?.upload_info)
          ? initResponse.upload_info
          : [];

        if (!sessionId) {
          throw new Error("Upload initialization failed: missing session id");
        }

        if (uploadInfo.length === 0) {
          throw new Error("Upload initialization failed: no files returned by server");
        }

        this.sessionId = sessionId;

        const localFiles = Array.isArray(this.files) ? this.files : [];
        this.totalBytes = localFiles.reduce((sum, f) => sum + (f.size || 0), 0);

        // Setup file states
        const originalRelPath = (f) =>
          (f.webkitRelativePath && f.webkitRelativePath.length > 0)
            ? f.webkitRelativePath
            : f.name;

        const fileMap = new Map(localFiles.map((f) => [originalRelPath(f), f]));

        this.fileStates = uploadInfo.map((info) => ({
          blob_path: info.blob_path,
          filename: info.filename || info.relative_path,
          status: "pending",
          progress: 0,
          error: null,
          uploadInfo: info,
          fileObject:
            fileMap.get(info.original_relative_path || info.relative_path || info.filename) ||
            fileMap.get(info.filename),
          retrying: false,
        }));
        this.isInitializingUpload = false;

        // Upload files with bounded concurrency while preserving top-to-bottom queueing
        const maxConcurrentUploads = 8;
        let nextFileIndex = 0;

        const worker = async () => {
          while (nextFileIndex < this.fileStates.length) {
            const currentIndex = nextFileIndex;
            nextFileIndex += 1;
            await this.uploadAndCommitFile(this.fileStates[currentIndex]);
          }
        };

        const workerCount = Math.min(maxConcurrentUploads, this.fileStates.length);
        const workers = Array.from({ length: workerCount }, () => worker());
        await Promise.allSettled(workers);

        // Finalize session automatically if all succeeded
        const allSuccess = this.fileStates.every((f) => f.status === "committed");
        if (allSuccess) {
          await this.finalizeSession();
          this.$emit("complete");
          this.toast.success("All files uploaded successfully!");
        }
      } catch (error) {
        this.isInitializingUpload = false;
        console.error("Upload session failed:", error);
        this.toast.error("Failed to initialize upload session");
        this.$emit("error", error);
      } finally {
        this.isInitializingUpload = false;
      }
    },

    async uploadAndCommitFile(fileState) {
      try {
        if (!fileState.fileObject) {
          throw new Error(`Could not match local file for ${fileState.filename}`);
        }

        if (!fileState.uploadInfo?.upload_url) {
          throw new Error(`Missing upload URL for ${fileState.filename}`);
        }

        fileState.status = "uploading";
        fileState.progress = 0;
        fileState.error = null;

        // Upload to blob storage
        await apiService.uploadFileToBlob(
          fileState.uploadInfo.upload_url,
          fileState.fileObject,
          (loaded, total) => {
            fileState.progress = total > 0 ? Math.min(100, Math.floor((loaded / total) * 100)) : 0;
            this.updateOverallProgress();
          }
        );

        fileState.status = "uploaded";
        fileState.progress = 100;

        // Commit file
        await apiService.completeFileUpload(
          this.sessionId, 
          fileState.blob_path,
          this.fileType,
          this.entityId,
          this.entraId,
          this.folderId
        );

        fileState.status = "committed";
      } catch (error) {
        fileState.status = "failed";
        fileState.error = error.message || String(error);
        console.error(`Failed to upload ${fileState.filename}:`, error);
      }
    },

    async retryFile(fileState) {
      if (fileState.retrying) return;

      try {
        fileState.retrying = true;
        fileState.error = null;

        const now = new Date();
        const expiry = new Date(fileState.uploadInfo.expires_at);
        const needsRefresh = (expiry - now) < 60000;

        if (needsRefresh) {
          // Refresh URLs
          const { upload_info } = await apiService.refreshUploadUrls(
            this.sessionId,
            this.fileType,
            this.entityId
          );
          const refreshedInfo = (upload_info || []).find(
            (info) => info.blob_path === fileState.blob_path
          );
          if (refreshedInfo) {
            fileState.uploadInfo = refreshedInfo;
          }
        }

        // Retry upload and commit
        await this.uploadAndCommitFile(fileState);

        if (fileState.status === "committed") {
          this.toast.success(`${fileState.filename} uploaded successfully!`);
        }
      } catch (error) {
        this.toast.error(`Failed to retry ${fileState.filename}`);
      } finally {
        fileState.retrying = false;
      }
    },

    updateOverallProgress() {
      const totalSize = this.fileStates.reduce(
        (sum, f) => sum + (f.fileObject?.size || 0),
        0
      );

      if (totalSize === 0) {
        this.overallPercent = 0;
        return;
      }

      const uploadedSize = this.fileStates.reduce((sum, f) => {
        const fileSize = f.fileObject?.size || 0;
        if (f.status === "committed") {
          return sum + fileSize;
        } else if (f.status === "uploading") {
          return sum + (fileSize * f.progress) / 100;
        }
        return sum;
      }, 0);

      this.overallPercent = Math.min(100, Math.floor((uploadedSize / totalSize) * 100));
    },

    async finalizeSession() {
      if (this.sessionId) {
        try {
          await apiService.finalizeUploadSession(
            this.sessionId,
            this.entraId,
            this.fileType,
            this.entityId
          );
        } catch (error) {
          console.error("Failed to finalize session:", error);
        }
      }
    },

    async handleFinalize() {
      await this.finalizeSession();
      this.$emit("complete");
      this.$emit("close");
    },

    handleOverlayClick() {
      if (!this.isActivelyUploading) {
        this.handleClose();
      }
    },

    async handleClose() {
      if (this.isActivelyUploading) {
        return; // Don't allow closing while uploading
      }

      // Finalize session if not already done
      if (this.sessionId) {
        await this.finalizeSession();
      }

      this.$emit("close");
    },

    getStatusText(file) {
      switch (file.status) {
        case "pending":
          return "Waiting...";
        case "uploading":
          return `Uploading... ${file.progress}%`;
        case "uploaded":
          return "Committing...";
        case "committed":
          return "Complete ✓";
        case "failed":
          return "Failed";
        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.upload-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px 8px;
  transition: color 0.2s;
}

.close-btn:hover:not(:disabled) {
  color: #333;
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.overall-progress {
  margin-bottom: 24px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.status-text {
  color: #333;
  font-weight: 500;
}

.percent-text {
  color: #666;
  font-weight: 600;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #66bb6a);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: border-color 0.2s;
}

.file-item.status-committed {
  background: #f1f8f4;
  border-color: #4caf50;
}

.file-item.status-failed {
  background: #fff5f5;
  border-color: #f44336;
}

.file-item.status-uploading {
  border-color: #2196f3;
}

.file-icon {
  font-size: 24px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-icon .bi-file-earmark {
  color: #999;
}

.file-icon .bi-arrow-repeat.spinner {
  color: #2196f3;
  animation: spin 1s linear infinite;
}

.init-upload-spinner {
  color: #2196f3;
  animation: spin 1s linear infinite;
  margin-left: 2px;
}

.file-icon .bi-check-circle-fill {
  color: #4caf50;
}

.file-icon .bi-exclamation-circle-fill {
  color: #f44336;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

.file-status-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.file-progress-bar {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 6px;
}

.file-progress-fill {
  height: 100%;
  background: #2196f3;
  transition: width 0.3s ease;
  border-radius: 2px;
}

.file-error {
  font-size: 12px;
  color: #f44336;
  margin-top: 4px;
}

.file-actions {
  display: flex;
  align-items: center;
}

.retry-btn {
  background: none;
  border: 1px solid #2196f3;
  color: #2196f3;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}

.retry-btn:hover:not(:disabled) {
  background: #2196f3;
  color: white;
}

.retry-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.btn-secondary {
  padding: 10px 20px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: #1976d2;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
